import React from 'react';
import { motion } from 'framer-motion';
import { FaInstagram, FaYoutube, FaTiktok, FaStar, FaUsers, FaEye, FaDollarSign } from 'react-icons/fa';
import { API_ENDPOINTS, getImageUrl } from '../../utils/GlobalAPI';

const HireMeForm = ({ onClose, influencerId, influencerData, isPremiumInfluencer = false, premiumInfluencerId = null }) => {
  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = {
      name: e.target.name.value,
      phone: e.target.phone.value,
      email: e.target.email.value,
      influencerId: influencerId || null, // include the ID if available
      isPremiumInfluencer: isPremiumInfluencer || false, // mark as premium influencer
      premiumInfluencerId: premiumInfluencerId || null, // store premium influencer ID
    };

    try {
      const res = await fetch(API_ENDPOINTS.HIRE_REQUESTS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (res.ok) {
        alert('Request submitted successfully');
        onClose?.(); // Close the modal on success
      } else {
        const errorData = await res.json();
        alert('Error: ' + errorData.error);
      }
    } catch (err) {
      alert('Something went wrong: ' + err.message);
    }
  };

  // Helper function to get influencer data with fallbacks
  const getInfluencerData = (field) => {
    if (!influencerData) return 'N/A';
    
    const fieldMappings = {
      name: influencerData.influencerName || influencerData.name || 'Unknown Influencer',
      bio: influencerData.bio || 'No bio available',
      image: influencerData.influencerImage ? getImageUrl(influencerData.influencerImage) : 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png',
      instaSubs: influencerData.instaSubs,
      engagement: influencerData.engagementPercentage ? `${influencerData.engagementPercentage}%` : 'N/A',
      avgViews: influencerData.averageViews ? `${influencerData.averageViews}k` : 'N/A',
      clients: Array.isArray(influencerData.clients) 
        ? influencerData.clients 
        : (influencerData.brandsWorkedWith ? influencerData.brandsWorkedWith.split(',') : []),
      price: influencerData.price ? `₹${influencerData.price}/hire` : 'Contact for pricing',
      category: influencerData.category || 'Other',
      awards: Array.isArray(influencerData.awards) ? influencerData.awards : []
    };
    
    return fieldMappings[field];
  };

  // Platform icon component
  const PlatformIcon = ({ platform }) => {
    const platformIcons = {
      instagram: <FaInstagram className="inline ml-1 text-pink-600" />,
      youtube: <FaYoutube className="inline ml-1 text-red-600" />,
      tiktok: <FaTiktok className="inline ml-1 text-black" />
    };
    
    const normalizedPlatform = platform?.toLowerCase() || 'instagram';
    return platformIcons[normalizedPlatform] || platformIcons.instagram;
  };

  return (
    <div className="flex flex-col lg:flex-row h-full max-h-[85vh] sm:max-h-[85vh] lg:max-h-[85vh] xl:max-h-[85vh]">
      {/* Left Side - Influencer Details */}
      <div className="w-full lg:w-1/2 bg-gradient-to-br from-blue-50 to-purple-50 border-b lg:border-b-0 lg:border-r border-gray-200 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 min-h-0 relative">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col p-4 sm:p-6 pb-16"
        >
          {/* Influencer Header */}
          <div className="text-center mb-4 sm:mb-6">
            <div className="relative inline-block">
              <img
                src={getInfluencerData('image')}
                alt={getInfluencerData('name')}
                className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full object-cover mx-auto mb-2 sm:mb-3 shadow-lg border-2 sm:border-4 border-white"
              />
              <div className="absolute -bottom-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                <FaStar className="w-2 h-2 sm:w-2.5 sm:h-2.5 lg:w-3 lg:h-3 text-white" />
              </div>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-1">{getInfluencerData('name')}</h3>
            <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2">{getInfluencerData('category')} Creator</p>
            <div className="flex items-center justify-center text-xs sm:text-sm text-gray-500">
              <FaInstagram className="w-3 h-3 sm:w-4 sm:h-4 text-pink-600 mr-1" />
              <span>{getInfluencerData('instaSubs')} followers</span>
            </div>
          </div>

          {/* Bio */}
          <div className="mb-4 sm:mb-6">
            <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">About</h4>
            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed line-clamp-3 sm:line-clamp-none">{getInfluencerData('bio')}</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4 sm:mb-6">
            <div className="bg-white rounded-lg p-2 sm:p-3 text-center shadow-sm">
              <div className="flex items-center justify-center mb-1">
                <FaUsers className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 mr-1" />
                <span className="text-xs text-gray-600">Engagement</span>
              </div>
              <p className="text-sm sm:text-lg font-bold text-gray-800">{getInfluencerData('engagement')}</p>
            </div>
            <div className="bg-white rounded-lg p-2 sm:p-3 text-center shadow-sm">
              <div className="flex items-center justify-center mb-1">
                <FaEye className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 mr-1" />
                <span className="text-xs text-gray-600">Avg Views</span>
              </div>
              <p className="text-sm sm:text-lg font-bold text-gray-800">{getInfluencerData('avgViews')}</p>
            </div>
            <div className="bg-white rounded-lg p-2 sm:p-3 text-center shadow-sm">
              <div className="flex items-center justify-center mb-1">
                <FaUsers className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-600 mr-1" />
                <span className="text-xs text-gray-600">Followers</span>
              </div>
              <p className="text-sm sm:text-lg font-bold text-gray-800">{getInfluencerData('instaSubs')}</p>
            </div>
            <div className="bg-white rounded-lg p-2 sm:p-3 text-center shadow-sm">
              <div className="flex items-center justify-center mb-1">
                <FaStar className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600 mr-1" />
                <span className="text-xs text-gray-600">Rate</span>
              </div>
              <p className="text-sm sm:text-lg font-bold text-gray-800">{getInfluencerData('price')}</p>
            </div>
          </div>

          {/* Brands Worked With */}
          {getInfluencerData('clients').length > 0 && (
            <div className="mb-4 sm:mb-6">
              <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">Brands Worked With</h4>
              <div className="flex flex-wrap gap-1 sm:gap-2">
                {getInfluencerData('clients').slice(0, 4).map((brand, index) => (
                  <span
                    key={index}
                    className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-white rounded-full text-xs text-gray-600 shadow-sm border"
                  >
                    {brand}
                  </span>
                ))}
                {getInfluencerData('clients').length > 4 && (
                  <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-gray-100 rounded-full text-xs text-gray-500">
                    +{getInfluencerData('clients').length - 4} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Awards */}
          {getInfluencerData('awards').length > 0 && (
            <div className="mb-6 sm:mb-8">
              <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Awards & Recognition</h4>
              <div className="space-y-2">
                {getInfluencerData('awards').map((award, index) => (
                  <div key={index} className="flex items-center text-xs text-gray-600">
                    <FaStar className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-yellow-500 mr-2 flex-shrink-0" />
                    <span className="leading-relaxed">{award}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Scroll indicator */}
          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-blue-50 to-transparent pointer-events-none"></div>
        </motion.div>
      </div>

      {/* Right Side - Hire Form */}
      <div className="w-full lg:w-1/2 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 min-h-0">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex flex-col p-4 sm:p-6 pb-12"
        >
                <div className="mb-4 sm:mb-6">
            <div className="flex items-center gap-2 mb-1 sm:mb-2">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800">Hire {getInfluencerData('name')}</h2>
              {isPremiumInfluencer && (
                <span className="bg-gradient-to-r from-yellow-400 to-amber-500 text-yellow-900 px-2 py-1 rounded-full text-xs font-bold shadow-md">
                  ⭐ PREMIUM
                </span>
              )}
            </div>
            <p className="text-xs sm:text-sm text-gray-600">
              {isPremiumInfluencer 
                ? 'Fill out the form below to hire our premium influencer and get started with your collaboration.'
                : 'Fill out the form below to get started with your collaboration.'}
            </p>
          </div>

          <form className="flex flex-col gap-3 sm:gap-4 flex-1" onSubmit={handleSubmit}>
          <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Your Name</label>
            <input
              type="text"
              name="name"
                className="w-full border border-gray-300 rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base text-gray-900 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors placeholder:text-gray-500"
                placeholder="Enter your full name"
              required
            />
          </div>

          <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Phone Number</label>
            <input
              type="tel"
              name="phone"
                className="w-full border border-gray-300 rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base text-gray-900 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors placeholder:text-gray-500"
                placeholder="Enter your phone number"
              required
            />
          </div>

          <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Email Address</label>
            <input
              type="email"
              name="email"
                className="w-full border border-gray-300 rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base text-gray-900 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors placeholder:text-gray-500"
                placeholder="Enter your email address"
              required
            />
          </div>

            <div className="mt-auto">
          <button
            type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-2 sm:py-3 text-sm sm:text-base rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
                Send Hire Request
          </button>
            </div>
        </form>
        </motion.div>
      </div>
    </div>
  );
};

export default HireMeForm;
